import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/lib/images";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const STORY_IMAGES = [IMG.morningLake, IMG.longevityStory, IMG.sauna];

export const Storytelling = () => {
  const { t } = useLang();
  return (
    <section
      id="stories"
      data-testid="stories-section"
      className="relative py-28 md:py-40 bg-ivory text-forest"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          variants={stagger}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-24 md:mb-36"
          data-testid="stories-intro"
        >
          <motion.div variants={fadeUp} className="lg:col-span-5">
            <p className="overline text-bronze mb-6" data-testid="stories-eyebrow">
              {t.stories.eyebrow}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {t.stories.title}
            </h2>
          </motion.div>
          <motion.div variants={fadeUp} className="lg:col-span-6 lg:col-start-7">
            <p className="text-base md:text-lg leading-relaxed text-forest/80">{t.stories.intro_1}</p>
            {t.stories.intro_2 && (
              <p className="mt-6 overline text-bronze">{t.stories.intro_2}</p>
            )}
          </motion.div>
        </motion.div>

        <div className="flex flex-col gap-32 md:gap-44">
          {t.stories.items.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <motion.article
                key={i}
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  reverse ? "lg:[direction:rtl]" : ""
                }`}
                data-testid={`story-${i + 1}`}
              >
                <motion.div
                  variants={fadeUp}
                  className={`lg:col-span-7 ${reverse ? "lg:[direction:ltr]" : ""}`}
                >
                  <div className="aspect-[5/4] w-full overflow-hidden">
                    <img
                      src={STORY_IMAGES[i]}
                      alt={s.alt || s.title}
                      className="w-full h-full object-cover hover-img"
                    />
                  </div>
                </motion.div>
                <motion.div
                  variants={fadeUp}
                  className={`lg:col-span-5 ${reverse ? "lg:[direction:ltr]" : ""}`}
                >
                  <p className="overline text-bronze mb-5">{s.tag}</p>
                  <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.1] tracking-tight">
                    {s.title}
                  </h3>
                  {s.statement && (
                    <p className="mt-5 font-serif italic text-xl md:text-2xl text-forest/85 leading-snug">
                      {s.statement}
                    </p>
                  )}
                  <div
                    className="mt-6 w-10 h-[2px]"
                    style={{ background: "var(--yz-bronze)" }}
                    aria-hidden="true"
                  />
                  <div className="mt-6 text-base md:text-lg leading-relaxed text-forest/75 max-w-md space-y-4">
                    {s.body.split("\n\n").map((para, k) => (
                      <p key={k}>{para}</p>
                    ))}
                  </div>
                </motion.div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
